import type {
  AssetRecord,
  ListOptions,
  ListResult,
  ManifestEnvelope,
  ProjectRecord,
  ProjectStorageAdapter,
  ProjectSummary,
  Publication,
  VersionRecord,
} from '@lightscape/storage-adapter'
import { ProjectNotFoundError, StorageAdapterError } from '@lightscape/storage-adapter'
import { ApiError } from '../api/apiError'
import { assetsControllerUpload } from '../api/generated/api/assets'
import { exportControllerExport } from '../api/generated/api/export'
import { importControllerImport } from '../api/generated/api/import'
import {
  projectsControllerCreate,
  projectsControllerDelete,
  projectsControllerGet,
  projectsControllerList,
  projectsControllerUpdate,
} from '../api/generated/api/projects'
import { publishControllerPublishProject } from '../api/generated/api/publish'
import { restoreControllerRestore } from '../api/generated/api/restore'
import { versionsControllerList, versionsControllerSnapshot } from '../api/generated/api/versions'

/**
 * Owner tokens from before per-person ownership. The server claims a legacy project for the first
 * signed-in person who presents its token, so we keep sending them until each project is claimed.
 */
const legacyOwnerToken = (projectId: string): Record<string, string> | undefined => {
  try {
    const token = localStorage.getItem(`lightscape:owner:${projectId}`)
    return token ? { 'X-Owner-Token': token } : undefined
  } catch {
    return undefined
  }
}

/** The scene changed elsewhere (the AI assistant, another tab) since this tab loaded it. */
export class RevisionConflictError extends StorageAdapterError {
  constructor(message: string) {
    super('REVISION_CONFLICT', message, 409)
    this.name = 'RevisionConflictError'
  }
}

const rethrow = (error: unknown, projectId?: string): never => {
  if (error instanceof ApiError) {
    if (error.status === 404 && projectId) throw new ProjectNotFoundError(projectId)
    if (error.code === 'REVISION_CONFLICT') throw new RevisionConflictError(error.message)
  }
  throw error
}

export class ServerProjectStorageAdapter implements ProjectStorageAdapter {
  private readonly baseUrl: string
  /** Last draft revision this tab saw per project; sent with saves to detect lost updates. */
  private readonly revisions = new Map<string, string>()

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  revisionOf(projectId: string): string | undefined {
    return this.revisions.get(projectId)
  }

  async createProject(input: {
    title?: string
    draftManifest: ManifestEnvelope
  }): Promise<{ project: ProjectRecord }> {
    const data = (await projectsControllerCreate(
      input as unknown as API.CreateProjectDto,
    )) as { project: ProjectRecord }
    return { project: this.track(data.project) }
  }

  async listProjects(opts: ListOptions = {}): Promise<ListResult<ProjectSummary>> {
    const result = (await projectsControllerList({
      page: opts.page,
      pageSize: opts.pageSize,
      q: opts.q,
    } as API.ProjectsControllerListParams)) as ListResult<ProjectSummary>
    return { ...result, items: result.items.map((item) => this.normalizeProjectSummary(item)) }
  }

  async getProject(id: string): Promise<ProjectRecord> {
    const record = (await projectsControllerGet({ id }, { headers: legacyOwnerToken(id) }).catch(
      (error: unknown) => rethrow(error, id),
    )) as ProjectRecord
    return this.track(record)
  }

  async updateProject(
    id: string,
    patch: { title?: string; draftManifest?: ManifestEnvelope },
  ): Promise<ProjectRecord> {
    const body = {
      ...patch,
      baseRevision: patch.draftManifest ? this.revisions.get(id) : undefined,
    } as unknown as API.UpdateProjectDto
    const record = (await projectsControllerUpdate({ id }, body, {
      headers: legacyOwnerToken(id),
    }).catch((error: unknown) => rethrow(error, id))) as ProjectRecord
    return this.track(record)
  }

  async deleteProject(id: string): Promise<void> {
    await projectsControllerDelete({ id }, { headers: legacyOwnerToken(id) }).catch(
      (error: unknown) => rethrow(error, id),
    )
    this.revisions.delete(id)
  }

  async snapshotVersion(projectId: string, opts: { message?: string } = {}) {
    return (await versionsControllerSnapshot({ projectId }, opts, {
      headers: legacyOwnerToken(projectId),
    })) as VersionRecord
  }

  async listVersions(projectId: string): Promise<VersionRecord[]> {
    const data = (await versionsControllerList(
      { projectId, page: 1, pageSize: 50 } as API.VersionsControllerListParams,
      { headers: legacyOwnerToken(projectId) },
    )) as ListResult<VersionRecord>
    return data.items
  }

  async restoreVersion(projectId: string, versionId: string): Promise<ProjectRecord> {
    await restoreControllerRestore(
      { projectId, versionId },
      { headers: legacyOwnerToken(projectId) },
    )
    return this.getProject(projectId)
  }

  async publish(
    projectId: string,
    opts: { thumbnailPng: Blob; message?: string },
  ): Promise<Publication> {
    const thumbnail = new File([opts.thumbnailPng], 'thumbnail.png', { type: 'image/png' })
    return (await publishControllerPublishProject(
      { projectId },
      { message: opts.message } as never,
      thumbnail,
      { headers: legacyOwnerToken(projectId) },
    )) as Publication
  }

  async uploadAsset(blob: Blob, filename?: string): Promise<AssetRecord> {
    const file = blob instanceof File ? blob : new File([blob], filename ?? 'upload.png', { type: blob.type })
    return (await assetsControllerUpload({}, file)) as AssetRecord
  }

  assetUrl(hash: string): string {
    return `${this.baseUrl}/assets/${hash}`
  }

  async materializeAssetUrl(hash: string): Promise<string> {
    return this.assetUrl(hash)
  }

  async importPackage(file: File): Promise<{ project: ProjectRecord }> {
    const data = (await importControllerImport({}, file)) as { project: ProjectRecord }
    return { project: this.track(data.project) }
  }

  async exportProject(id: string, format: 'json' | 'zip' = 'json'): Promise<Blob> {
    return (await exportControllerExport(
      { projectId: id, format } as API.ExportControllerExportParams,
      { responseType: 'blob', headers: legacyOwnerToken(id) },
    )) as Blob
  }

  private track(record: ProjectRecord): ProjectRecord {
    if (record.draftUpdatedAt) this.revisions.set(record.id, record.draftUpdatedAt)
    return this.normalizeProjectSummary(record) as ProjectRecord
  }

  /** Asset URLs are rebuilt from their hash, so links minted under an older API host keep working. */
  private normalizeAssetUrl(url: string | undefined): string | undefined {
    if (!url) return undefined
    const hash = /\/assets\/([a-f0-9]{64})/.exec(url)?.[1]
    return hash ? this.assetUrl(hash) : url
  }

  private normalizeProjectSummary(project: ProjectSummary): ProjectSummary {
    const thumbnailHash =
      'thumbnailHash' in project && typeof project.thumbnailHash === 'string'
        ? project.thumbnailHash
        : undefined
    return {
      ...project,
      thumbnailUrl:
        this.normalizeAssetUrl(project.thumbnailUrl) ??
        (thumbnailHash ? this.assetUrl(thumbnailHash) : undefined),
    }
  }
}
